import { openChatLink } from "./utils";

const SURVEY_PLACEMENT = "end_chat_button";

export const hasNativeSurveyLaunchBridge = ({ currentWindow = window } = {}) => {
  const android = currentWindow.android;
  const androidIsAvailable =
    typeof android?.closeBotWithSurvey === "function" ||
    typeof android?.openURL === "function" ||
    typeof android?.botEvents === "function" ||
    typeof android?.closeBot === "function";

  if (androidIsAvailable) {
    return (
      typeof android.closeBotWithSurvey === "function" ||
      typeof android.openURL === "function" ||
      typeof android.botEvents === "function"
    );
  }

  const messageHandlers = currentWindow.webkit?.messageHandlers;
  return (
    typeof messageHandlers?.closeBotWithSurvey?.postMessage === "function" ||
    typeof messageHandlers?.openURL?.postMessage === "function" ||
    typeof messageHandlers?.botEvents?.postMessage === "function"
  );
};

export const normalizeSurveyUrl = (value) => {
  if (typeof value !== "string" || !value.trim() || value.length > 2048) {
    return null;
  }

  try {
    const url = new URL(value.trim());

    if (
      url.username ||
      url.password ||
      url.hash ||
      url.protocol !== "https:" ||
      url.port
    ) {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
};

export const launchValidatedSurveyLink = (
  value,
  {
    currentWindow = window,
    openWindow = (...args) => currentWindow.open(...args),
    requireWindowHandle = false,
  } = {},
) => {
  const surveyURL = normalizeSurveyUrl(value);
  if (!surveyURL || typeof currentWindow.open !== "function") {
    return false;
  }

  return openChatLink(surveyURL, {
    currentUrl: currentWindow.location?.href,
    openWindow,
    requireWindowHandle,
  });
};

export const launchValidatedSurveyAnchor = (
  value,
  { currentDocument = document } = {},
) => {
  const surveyURL = normalizeSurveyUrl(value);
  const documentBody = currentDocument?.body;

  if (
    !surveyURL ||
    typeof currentDocument?.createElement !== "function" ||
    typeof documentBody?.appendChild !== "function"
  ) {
    return false;
  }

  const surveyLink = currentDocument.createElement("a");
  surveyLink.href = surveyURL;
  surveyLink.target = "_blank";
  surveyLink.rel = "noopener noreferrer";
  surveyLink.hidden = true;

  try {
    documentBody.appendChild(surveyLink);
    surveyLink.click();
    return true;
  } catch {
    return false;
  } finally {
    surveyLink.remove();
  }
};

const buildNativeSurveyAction = (surveyURL) => ({
  type: "Action.OpenUrl",
  title: "quick survey",
  url: surveyURL,
  data: {
    linkSelected: "quick survey",
    surveyPlacement: SURVEY_PLACEMENT,
  },
});

const buildNativeSurveyCloseAction = (surveyURL) => {
  const surveyAction = buildNativeSurveyAction(surveyURL);

  return {
    title: surveyAction.title,
    surveyURL,
    url: surveyAction.url,
    data: surveyAction.data,
  };
};

const createSafeChatClosedEvent = (event, surveyURL) => ({
  ...event,
  payload: {
    ...(event?.payload ?? {}),
    surveyURL: surveyURL ?? "",
  },
});

const postWebEvent = (currentWindow, event, onError) => {
  try {
    currentWindow.parent?.postMessage(event, "*");
    return "web_parent";
  } catch (error) {
    onError("Unable to deliver CHAT_CLOSED to the web parent.", error);
    return "unavailable";
  }
};

const openLegacySurveyLink = (surveyURL, launchSurveyLink, channel, onError) => {
  if (!surveyURL) {
    return null;
  }

  try {
    return launchSurveyLink(surveyURL) ? channel : null;
  } catch (error) {
    onError("Unable to launch the survey as a web link.", error);
    return null;
  }
};

export const deliverChatClosedEvent = (
  event,
  {
    currentWindow = window,
    launchSurveyLink,
    onError = (...args) => console.error(...args),
    surveyAlreadyPresented = false,
  } = {},
) => {
  const surveyURL = normalizeSurveyUrl(event?.payload?.surveyURL);
  const safeEvent = createSafeChatClosedEvent(event, surveyURL);
  const serializedEvent = JSON.stringify(safeEvent);
  const shouldPresentSurvey = Boolean(surveyURL && !surveyAlreadyPresented);
  const serializedSurveyAction = shouldPresentSurvey
    ? JSON.stringify(buildNativeSurveyAction(surveyURL))
    : null;
  const serializedSurveyCloseAction = shouldPresentSurvey
    ? JSON.stringify(buildNativeSurveyCloseAction(surveyURL))
    : null;
  const launchSurvey =
    typeof launchSurveyLink === "function"
      ? launchSurveyLink
      : (url) =>
          launchValidatedSurveyLink(url, {
            currentWindow,
            requireWindowHandle: true,
          });
  const android = currentWindow.android;
  const androidCanClose = typeof android?.closeBot === "function";
  const androidIsAvailable =
    typeof android?.closeBotWithSurvey === "function" ||
    typeof android?.openURL === "function" ||
    typeof android?.botEvents === "function" ||
    typeof android?.closeBot === "function";

  if (androidIsAvailable) {
    if (
      serializedSurveyCloseAction &&
      typeof android.closeBotWithSurvey === "function"
    ) {
      try {
        android.closeBotWithSurvey(serializedSurveyCloseAction);
        return {
          channel: "android_close_bot_with_survey",
          closeHandled: true,
          event: safeEvent,
        };
      } catch (error) {
        onError("Unable to close Android chat with the survey.", error);
      }
    }

    if (serializedSurveyAction && typeof android.openURL === "function") {
      try {
        android.openURL(serializedSurveyAction);
        return {
          channel: "android_open_url",
          ...(androidCanClose ? { deferNativeClose: true } : {}),
          event: safeEvent,
        };
      } catch (error) {
        onError("Unable to launch the survey through Android openURL.", error);
      }
    }

    const androidWebLinkChannel = openLegacySurveyLink(
      surveyAlreadyPresented ? null : surveyURL,
      launchSurvey,
      "android_web_link",
      onError,
    );
    if (androidWebLinkChannel) {
      return {
        channel: androidWebLinkChannel,
        ...(androidCanClose ? { deferNativeClose: true } : {}),
        event: safeEvent,
      };
    }

    if (typeof android.botEvents === "function") {
      try {
        android.botEvents(serializedEvent);
        return { channel: "android_bot_events", event: safeEvent };
      } catch (error) {
        onError("Unable to deliver CHAT_CLOSED to Android botEvents.", error);
      }
    }

    if (surveyAlreadyPresented && surveyURL) {
      postWebEvent(currentWindow, safeEvent, onError);
      return { channel: "android_browser_link", event: safeEvent };
    }

    return {
      channel: postWebEvent(currentWindow, safeEvent, onError),
      event: safeEvent,
    };
  }

  const messageHandlers = currentWindow.webkit?.messageHandlers;
  const iosCloseBotWithSurvey = messageHandlers?.closeBotWithSurvey;
  const iosOpenURL = messageHandlers?.openURL;
  const iosBotEvents = messageHandlers?.botEvents;
  const iosCanClose =
    typeof messageHandlers?.closeBot?.postMessage === "function";
  const iosIsAvailable =
    typeof iosCloseBotWithSurvey?.postMessage === "function" ||
    typeof iosOpenURL?.postMessage === "function" ||
    typeof iosBotEvents?.postMessage === "function" ||
    typeof messageHandlers?.closeBot?.postMessage === "function";

  if (iosIsAvailable) {
    if (
      serializedSurveyCloseAction &&
      typeof iosCloseBotWithSurvey?.postMessage === "function"
    ) {
      try {
        iosCloseBotWithSurvey.postMessage(serializedSurveyCloseAction);
        return {
          channel: "ios_close_bot_with_survey",
          closeHandled: true,
          event: safeEvent,
        };
      } catch (error) {
        onError("Unable to close iOS chat with the survey.", error);
      }
    }

    if (
      serializedSurveyAction &&
      typeof iosOpenURL?.postMessage === "function"
    ) {
      try {
        iosOpenURL.postMessage(serializedSurveyAction);
        return {
          channel: "ios_open_url",
          ...(iosCanClose ? { deferNativeClose: true } : {}),
          event: safeEvent,
        };
      } catch (error) {
        onError("Unable to launch the survey through iOS openURL.", error);
      }
    }

    const iosWebLinkChannel = openLegacySurveyLink(
      surveyAlreadyPresented ? null : surveyURL,
      launchSurvey,
      "ios_web_link",
      onError,
    );
    if (iosWebLinkChannel) {
      return {
        channel: iosWebLinkChannel,
        ...(iosCanClose ? { deferNativeClose: true } : {}),
        event: safeEvent,
      };
    }

    if (typeof iosBotEvents?.postMessage === "function") {
      try {
        iosBotEvents.postMessage(serializedEvent);
        return { channel: "ios_bot_events", event: safeEvent };
      } catch (error) {
        onError("Unable to deliver CHAT_CLOSED to iOS botEvents.", error);
      }
    }

    if (surveyAlreadyPresented && surveyURL) {
      postWebEvent(currentWindow, safeEvent, onError);
      return { channel: "ios_browser_link", event: safeEvent };
    }
  }

  return {
    channel: postWebEvent(currentWindow, safeEvent, onError),
    event: safeEvent,
  };
};
